import { type SimBalanceResponse } from "@/hooks/useSimTokenInfo";
import { addressEquals } from "@/lib/utils/address";
import { getChainInfo, getNativeTokenDecimals } from "@/lib/chains";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { CHAIN_TAG_MAP } from "@/lib/constants/chain-tags";
import { HL_CHAIN_ID, HL_USDC_ADDRESS, HL_USDC_DECIMALS, parseHlSpotUsdc } from "@/lib/hyperliquid/constants";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import type { HyperliquidClearinghouseState, HyperliquidSpotClearinghouseState } from "@/lib/hyperliquid/types";
import type { OrganizationWallet } from "@/types/wallet";

export interface NodeData {
  type?: string;
  payload?: Record<string, unknown>;
  connector?: {
    id?: string;
    walletAddress?: string;
  };
}

export type OrgWallet = Pick<OrganizationWallet, "id" | "address" | "isDefault" | "accountType" | "providerType">;

export interface MissingToken {
  address: string;
  chainId: number;
  symbol: string;
  reason?: string;
}

interface CalculateMissingTokensOptions {
  coveredNativeGasChainIds?: number[];
}

/**
 * Calculate which required tokens a wallet is missing based on balance data.
 * Shared between computeNodeGasStatus and NodeEditorSidebar.
 */
export function calculateMissingTokens(
  requiredTokens: MissingToken[] | undefined,
  balances: Array<{ chain_id: number; address: string; amount: string }> | undefined,
  options?: CalculateMissingTokensOptions,
): MissingToken[] {
  const coveredNativeGasChainIds = new Set(options?.coveredNativeGasChainIds ?? []);

  if (!requiredTokens || requiredTokens.length === 0 || !balances) return [];
  return requiredTokens.filter(token => {
    if (isNativeTokenAddress(token.address) && coveredNativeGasChainIds.has(token.chainId)) {
      return false;
    }

    const balance = balances.find(
      b =>
        b.chain_id === token.chainId &&
        (isNativeTokenAddress(token.address)
          ? isNativeTokenAddress(b.address)
          : addressEquals(b.address, token.address)),
    );
    if (!balance) return true;
    const amount = BigInt(balance.amount || "0");
    return amount === BigInt(0);
  });
}

export interface NodeGasStatus {
  requiresGas: boolean;
  chainId: number | undefined;
  chainInfo: { name?: string; gasToken?: string } | null;
  hasGas: boolean;
  hasLowGas: boolean;
  gasBalance: { amount: string; symbol: string } | null;
  isLoading: boolean;
  walletAddress: string | undefined;
  needsGasWarning: boolean;
  autoSwapEnabled: boolean;
  autoSwapCanCoverGas: boolean;
  missingTokens: MissingToken[];
  needsTokenWarning: boolean;
}

function parseChainId(val: unknown): number | undefined {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? undefined : parsed;
  }
  if (typeof val === "object" && val !== null && "id" in val) {
    const objId = (val as { id: unknown }).id;
    if (typeof objId === "number") return objId;
    if (typeof objId === "string") {
      const parsed = parseInt(objId, 10);
      return isNaN(parsed) ? undefined : parsed;
    }
  }
  return undefined;
}

export function extractChainIdFromPayload(payload: Record<string, unknown> | undefined): number | undefined {
  if (!payload) return undefined;

  const chainIdVal = parseChainId(payload.chainId);
  if (chainIdVal !== undefined) return chainIdVal;

  const chainVal = parseChainId(payload.chain);
  if (chainVal !== undefined) return chainVal;

  const chainIdSnake = parseChainId(payload.chain_id);
  if (chainIdSnake !== undefined) return chainIdSnake;

  if (Array.isArray(payload.chainIds) && payload.chainIds.length > 0) {
    const firstChainId = parseChainId(payload.chainIds[0]);
    if (firstChainId !== undefined) return firstChainId;
  }

  const srcChainId = parseChainId(payload.srcChainId);
  if (srcChainId !== undefined) return srcChainId;

  return undefined;
}

export function inferChainIdFromTags(definition: ActionTriggerDefinition | null | undefined): number | undefined {
  if (!definition?.tags) return undefined;

  const tags = definition.tags.map(t => t.toLowerCase());

  for (const [tagName, chainId] of Object.entries(CHAIN_TAG_MAP)) {
    if (tags.includes(tagName)) {
      return chainId;
    }
  }

  return undefined;
}

export function extractWalletAddress(
  nodeData: NodeData | null | undefined,
  orgWallets: OrgWallet[],
  connectorWallet?: OrgWallet,
): string | undefined {
  if (!nodeData) return undefined;

  const connector = nodeData.connector;

  if (typeof connector?.walletAddress === "string") return connector.walletAddress;

  if (connectorWallet?.address) return connectorWallet.address;

  const payload = nodeData.payload;
  if (!payload) return undefined;

  if (typeof payload.walletAddress === "string") return payload.walletAddress;
  if (typeof payload.wallet_address === "string") return payload.wallet_address;
  if (typeof payload.fromAddress === "string") return payload.fromAddress;
  if (typeof payload.from_address === "string") return payload.from_address;
  if (typeof payload.senderAddress === "string") return payload.senderAddress;

  const defaultWallet = orgWallets.find(w => w.isDefault) || orgWallets[0];
  return defaultWallet?.address;
}

export interface NodeGasRequirements {
  requiresGas: boolean;
  chainId: number | undefined;
  chainInfo: { name?: string; gasToken?: string } | null;
  walletAddress: string | undefined;
  requiredTokens: MissingToken[] | undefined;
  needsHlBalance: boolean;
  fetchChainIds: number[] | undefined;
  isGasSponsored: boolean;
}

export function getNodeGasRequirements(
  nodeData: NodeData | null | undefined,
  definition: ActionTriggerDefinition | null | undefined,
  orgWallets: OrgWallet[],
): NodeGasRequirements {
  const requiresGas = !!definition?.requiresGas;

  const payloadChainId = extractChainIdFromPayload(nodeData?.payload);
  const chainId = payloadChainId ?? definition?.gasChainId ?? inferChainIdFromTags(definition);

  const chainInfo = chainId ? getChainInfo(chainId) : null;

  // Resolve the connector wallet once and reuse for both walletAddress lookup
  // and gas-sponsor detection.
  const connectorId = nodeData?.connector?.id;
  const connectorWallet = connectorId ? orgWallets.find(w => w.id === connectorId) : undefined;
  const walletAddress = extractWalletAddress(nodeData, orgWallets, connectorWallet);

  const requiredTokens = definition?.requiredTokens;
  const needsHlBalance = !!requiredTokens?.some(t => t.chainId === HL_CHAIN_ID);

  const chainIdSet = new Set<number>();
  if (chainId && requiresGas) chainIdSet.add(chainId);
  if (requiredTokens) {
    for (const t of requiredTokens) {
      if (t.chainId !== HL_CHAIN_ID) chainIdSet.add(t.chainId);
    }
  }
  // Sort so two nodes with the same chain set produce identical TanStack
  // query keys regardless of iteration order, ensuring HTTP-level dedup.
  const fetchChainIds = chainIdSet.size > 0 ? [...chainIdSet].sort((a, b) => a - b) : undefined;

  const isGasSponsored = connectorWallet?.providerType === "session" && connectorWallet?.accountType === "smart_wallet";

  return {
    requiresGas,
    chainId,
    chainInfo,
    walletAddress,
    requiredTokens,
    needsHlBalance,
    fetchChainIds,
    isGasSponsored,
  };
}

export interface ComputeGasStatusInput {
  enabled: boolean;
  isLoadingWallets: boolean;
  balanceData: SimBalanceResponse | undefined;
  isLoadingBalance: boolean;
  autoSwapCoverage: { canAutoSwapGas: boolean; autoSwapEnabled: boolean; loading: boolean };
  hlState: HyperliquidClearinghouseState | undefined;
  hlSpot: HyperliquidSpotClearinghouseState | undefined;
  isLoadingHl: boolean;
}

export function computeNodeGasStatus(reqs: NodeGasRequirements, input: ComputeGasStatusInput): NodeGasStatus {
  const { requiresGas, chainId, chainInfo, walletAddress, requiredTokens, needsHlBalance, isGasSponsored } = reqs;
  const { enabled, isLoadingWallets, balanceData, isLoadingBalance, autoSwapCoverage, hlState, hlSpot, isLoadingHl } =
    input;

  let mergedBalances: Array<{ chain_id: number; address: string; amount: string }> = balanceData?.balances ?? [];
  if (needsHlBalance) {
    // totalRawUsd is raw collateral; we deliberately avoid `accountValue`,
    // which would include unrealized PnL — for a "does this wallet hold
    // funds" check we want deposited principal, not paper gains.
    const perpsUsd = parseFloat(hlState?.marginSummary?.totalRawUsd ?? "0");
    const spotUsdc = parseHlSpotUsdc(hlSpot);
    const totalHl = perpsUsd + spotUsdc;
    mergedBalances = [
      ...mergedBalances,
      {
        chain_id: HL_CHAIN_ID,
        address: HL_USDC_ADDRESS,
        amount: totalHl > 0 ? Math.floor(totalHl * 10 ** HL_USDC_DECIMALS).toString() : "0",
      },
    ];
  }

  let gasBalance: { amount: string; symbol: string } | null = null;
  if (balanceData?.balances && chainId) {
    const nativeBalance = balanceData.balances.find(b => b.chain_id === chainId && isNativeTokenAddress(b.address));
    if (!nativeBalance) {
      gasBalance = { amount: "0", symbol: chainInfo?.gasToken || "ETH" };
    } else {
      const decimals = nativeBalance.decimals || getNativeTokenDecimals(chainId);
      const amountBigInt = BigInt(nativeBalance.amount || "0");
      const divisor = BigInt(10 ** decimals);
      const wholePart = amountBigInt / divisor;
      const remainder = amountBigInt % divisor;

      let formattedAmount: string;
      if (remainder === BigInt(0)) {
        formattedAmount = wholePart.toString();
      } else {
        const decimalPart = remainder.toString().padStart(decimals, "0").slice(0, 6);
        if (wholePart === BigInt(0) && BigInt(decimalPart) === BigInt(0)) {
          formattedAmount = "< 0.000001";
        } else {
          const trimmedDecimalPart = decimalPart.replace(/0+$/, "");
          formattedAmount = trimmedDecimalPart ? `${wholePart}.${trimmedDecimalPart}` : wholePart.toString();
        }
      }
      gasBalance = { amount: formattedAmount, symbol: nativeBalance.symbol || chainInfo?.gasToken || "ETH" };
    }
  }

  const gasAmount = gasBalance ? parseFloat(gasBalance.amount) : NaN;
  const hasGas = !isNaN(gasAmount) && gasAmount > 0;
  const hasLowGas = !isNaN(gasAmount) && gasAmount > 0 && gasAmount < 0.001;

  const missingTokens = calculateMissingTokens(requiredTokens, mergedBalances, {
    coveredNativeGasChainIds: autoSwapCoverage.canAutoSwapGas && chainId ? [chainId] : [],
  });

  const needsGasWarning =
    enabled &&
    requiresGas &&
    !isGasSponsored &&
    !isLoadingWallets &&
    !isLoadingBalance &&
    !autoSwapCoverage.loading &&
    !hasGas &&
    !autoSwapCoverage.canAutoSwapGas;

  const needsTokenWarning =
    enabled &&
    !isLoadingWallets &&
    !isLoadingBalance &&
    !isLoadingHl &&
    !autoSwapCoverage.loading &&
    missingTokens.length > 0;

  const isLoading = isLoadingWallets || isLoadingBalance || isLoadingHl || autoSwapCoverage.loading;

  return {
    requiresGas,
    chainId,
    chainInfo,
    hasGas,
    hasLowGas,
    gasBalance,
    isLoading,
    walletAddress,
    needsGasWarning,
    autoSwapEnabled: autoSwapCoverage.autoSwapEnabled,
    autoSwapCanCoverGas: autoSwapCoverage.canAutoSwapGas,
    missingTokens,
    needsTokenWarning,
  };
}
